import * as THREE from 'three';
import {
  IYollukMotoru,
  IYollukEleman,
  IYollukDogrulama,
  IGostergeSonuc,
  ICozumGeometri,
  IDokumBolge,
} from './IDokumKontrol';
import { FikoGatingTasarim } from '../fiko-sph-sivi/analiz/FikoGatingTasarim';

const BESLEYICI_MODUL_KATSAYI = 1.2;       // besleyici, hedef sıcak noktadan en az %20 büyük modüle sahip olmalı
const BESLEYICI_MESAFE_KATSAYI = 4;        // besleyici çapının kaç katı uzaklık hâlâ "yakın" sayılır
const VARSAYILAN_CAP_MM = 30;
const VARSAYILAN_YUKSEKLIK_ORANI = 1.2;
const DELIK_CAP_MM = 6;
const DELIK_YUKSEKLIK_MM = 20;
const GIRIS_CAP_MM = 25;
const GIRIS_YUKSEKLIK_MM = 40;

export class FikoYollukYonetici implements IYollukMotoru {
  private readonly gating = new FikoGatingTasarim();
  private readonly _v = new THREE.Vector3();
  /** Parçanın en küçük kenarı (mm) — yolluk boyutlarını gerçekçi sınırlamak için */
  private parcaMinKenar = 100;
  private parcaMaxCap = 60;
  /** Kullanıcı ayarları: max besleyici sayısı + besleyici max çap (0 = otomatik) */
  private maxBesleyici = 6;
  private besleyiciMaxCapKullanici = 0;

  /** Yolluk öneri parametrelerini ayarla. */
  ayarla(ayar: { maxBesleyici?: number; besleyiciMaxCapMm?: number }): void {
    if (ayar.maxBesleyici != null && ayar.maxBesleyici > 0) this.maxBesleyici = Math.round(ayar.maxBesleyici);
    if (ayar.besleyiciMaxCapMm != null && ayar.besleyiciMaxCapMm > 0) this.besleyiciMaxCapKullanici = ayar.besleyiciMaxCapMm;
    else if (ayar.besleyiciMaxCapMm === 0) this.besleyiciMaxCapKullanici = 0;
  }

  oner(sonuc: IGostergeSonuc, geo: ICozumGeometri, alasim: string): IYollukEleman[] {
    const boyut = geo.bbox.max.clone().sub(geo.bbox.min);
    this.parcaMinKenar = Math.max(10, Math.min(boyut.x, boyut.y, boyut.z));
    // Besleyici çapı: kullanıcı ayarı veya makul otomatik (minKenar/4, üst 60 mm)
    this.parcaMaxCap = this.besleyiciMaxCapKullanici > 0
      ? this.besleyiciMaxCapKullanici
      : Math.min(Math.max(VARSAYILAN_CAP_MM, this.parcaMinKenar * 0.25), 60);
    const up = (geo.yukariYon ?? new THREE.Vector3(0, 1, 0)).clone().normalize();
    const elemanlar: IYollukEleman[] = [];
    // Yüzlerce sıcak nokta yerine: en kritik birkaçı, üst üste binmeden (min mesafe)
    const ayrim = Math.max(this.parcaMinKenar * 0.5, this.parcaMaxCap * 2);
    const sicakNoktalar = this.enKritikSec(sonuc.bolgeler.filter(b => b.tip === 'sicak_nokta'), this.maxBesleyici, ayrim);
    const havalar = this.enKritikSec(sonuc.bolgeler.filter(b => b.tip === 'hapsolan_hava'), Math.max(this.maxBesleyici, 4), this.parcaMinKenar * 0.4);

    const besleyiciOlcu = this.besleyiciOlculeri(geo, alasim, sicakNoktalar);

    for (let i = 0; i < sicakNoktalar.length; i++) {
      const b = sicakNoktalar[i];
      const olcu = besleyiciOlcu[i] ?? this.modulDenCap(b);
      const poz = b.merkez.clone().addScaledVector(up, olcu.yukseklik / 2); // bölge merkezinin yukarısı
      const eleman: IYollukEleman = {
        id: `besleyici-${b.id}`,
        tip: 'besleyici',
        pozisyon: poz,
        yon: up.clone(),
        capMm: olcu.cap,
        yukseklikMm: olcu.yukseklik,
        otomatikMi: true,
        kabulEdildi: false,
        gecerli: true,
        dogrulamaMesaji: '',
        hedefBolgeId: b.id,
      };
      const d = this.dogrula(eleman, sonuc, geo);
      eleman.gecerli = d.gecerli;
      eleman.dogrulamaMesaji = d.mesaj;
      elemanlar.push(eleman);
    }

    for (const h of havalar) {
      const eleman: IYollukEleman = {
        id: `hava-deligi-${h.id}`,
        tip: 'hava_deligi',
        pozisyon: h.merkez.clone(),
        yon: up.clone(),
        capMm: DELIK_CAP_MM,
        yukseklikMm: DELIK_YUKSEKLIK_MM,
        otomatikMi: true,
        kabulEdildi: false,
        gecerli: true,
        dogrulamaMesaji: '',
        hedefBolgeId: h.id,
      };
      const d = this.dogrula(eleman, sonuc, geo);
      eleman.gecerli = d.gecerli;
      eleman.dogrulamaMesaji = d.mesaj;
      elemanlar.push(eleman);
    }

    // Dolum girişi: parçanın yukarı yönündeki en üst noktası
    const merkez = geo.bbox.getCenter(this._v).clone();
    const boyutU = geo.bbox.max.clone().sub(geo.bbox.min);
    const yariUst = 0.5 * (Math.abs(boyutU.x * up.x) + Math.abs(boyutU.y * up.y) + Math.abs(boyutU.z * up.z));
    const ust = merkez.addScaledVector(up, yariUst);
    const giris: IYollukEleman = {
      id: 'dolum-girisi-ust',
      tip: 'dolum_girisi',
      pozisyon: ust,
      yon: up.clone().negate(), // akış aşağı (yerçekimi yönünde)
      debiGramSn: 100,
      capMm: GIRIS_CAP_MM,
      yukseklikMm: GIRIS_YUKSEKLIK_MM,
      otomatikMi: true,
      kabulEdildi: false,
      gecerli: true,
      dogrulamaMesaji: '',
    };
    const dg = this.dogrula(giris, sonuc, geo);
    giris.gecerli = dg.gecerli;
    giris.dogrulamaMesaji = dg.mesaj;
    elemanlar.push(giris);

    return elemanlar;
  }

  dogrula(eleman: IYollukEleman, sonuc: IGostergeSonuc, geo: ICozumGeometri): IYollukDogrulama {
    switch (eleman.tip) {
      case 'besleyici':
        return this.dogrulaBesleyici(eleman, sonuc);
      case 'hava_deligi':
        return this.dogrulaHavaDeligi(eleman, geo);
      case 'dolum_girisi':
        return this.dogrulaDolumGirisi(eleman, sonuc, geo);
      case 'sogutucu':
        return this.dogrulaSogutucu(eleman, sonuc);
      case 'maca':
        return { gecerli: true, mesaj: 'Maça: iç boşluk/kesit oluşturur (o bölge dolmaz) ✓' };
      case 'tasma':
        return this.dogrulaTasma(eleman, geo);
      case 'sensor':
        return { gecerli: true, mesaj: 'Sensör: noktada dolum/katılaşma süresi ölçer ✓' };
      case 'filtre':
      default:
        return { gecerli: true, mesaj: 'Filtre: cüruf/oksit tutar, türbülansı azaltır ✓' };
    }
  }

  /** Soğutucu sıcak noktaya yakınsa geçerli (yerel hızlı katılaşma). */
  private dogrulaSogutucu(eleman: IYollukEleman, sonuc: IGostergeSonuc): IYollukDogrulama {
    const sicaklar = sonuc.bolgeler.filter(b => b.tip === 'sicak_nokta');
    for (const b of sicaklar) {
      if (b.merkez.distanceTo(eleman.pozisyon) <= eleman.capMm * 2 + 30) {
        return { gecerli: true, mesaj: 'Sıcak noktayı hızlı katılaştırır — çekme azalır ✓' };
      }
    }
    return { gecerli: true, mesaj: 'Soğutucu: yerel ısıyı çeker (sıcak nokta yakınına koyun)' };
  }

  /** Taşma en üst (akışın son ulaştığı) bölgede olmalı — soğuk metal/cüruf toplar. */
  private dogrulaTasma(eleman: IYollukEleman, geo: ICozumGeometri): IYollukDogrulama {
    const up = (geo.yukariYon ?? new THREE.Vector3(0, 1, 0)).clone().normalize();
    const merkez = geo.bbox.getCenter(this._v);
    const ustte = eleman.pozisyon.clone().sub(merkez).dot(up) > 0;
    return ustte
      ? { gecerli: true, mesaj: 'Taşma üst bölgede — ilk/soğuk metali ve cürufu toplar ✓' }
      : { gecerli: true, mesaj: 'Taşma: en uzak/üst noktaya koyun (soğuk metal kapanı)' };
  }

  private dogrulaBesleyici(eleman: IYollukEleman, sonuc: IGostergeSonuc): IYollukDogrulama {
    const hedef = eleman.hedefBolgeId
      ? sonuc.bolgeler.find(b => b.id === eleman.hedefBolgeId)
      : undefined;
    if (!hedef) {
      return { gecerli: false, mesaj: 'Besleyici hedef sıcak noktayla eşleşmiyor' };
    }

    const hedefModulCm = hedef.birim === 'cm' ? hedef.deger : hedef.deger / 10;
    const besleyiciModulCm = (eleman.capMm / 4) / 10; // silindir besleyici modülü ≈ R/2 = cap/4 (mm→cm)

    const mesafe = eleman.pozisyon.distanceTo(hedef.merkez);
    const yakin = mesafe <= eleman.capMm * BESLEYICI_MESAFE_KATSAYI;
    const modulYeterli = besleyiciModulCm >= hedefModulCm * BESLEYICI_MODUL_KATSAYI;

    if (modulYeterli && yakin) {
      return { gecerli: true, mesaj: 'Sıcak noktayı besliyor ✓' };
    }
    if (!modulYeterli && !yakin) {
      return { gecerli: false, mesaj: 'Besleyici çok küçük ve uzak, çekme riski' };
    }
    if (!modulYeterli) {
      return { gecerli: false, mesaj: 'Besleyici çok küçük, sıcak nokta daha geç katılaşır — çekme riski' };
    }
    return { gecerli: false, mesaj: 'Besleyici sıcak noktadan uzak, besleme yetersiz' };
  }

  private dogrulaHavaDeligi(eleman: IYollukEleman, geo: ICozumGeometri): IYollukDogrulama {
    const yukseklik = geo.bbox.max.y - geo.bbox.min.y;
    const oran = yukseklik > 0 ? (eleman.pozisyon.y - geo.bbox.min.y) / yukseklik : 1;
    if (oran >= 0.75) {
      return { gecerli: true, mesaj: 'En üst hava cebinde, gaz tahliyesi ✓' };
    }
    if (oran >= 0.5) {
      return { gecerli: true, mesaj: 'Yüksek noktada, hava çıkışı uygun' };
    }
    return { gecerli: false, mesaj: 'Hava deliği alçakta, gaz hapsolur — üst noktaya taşıyın' };
  }

  private dogrulaDolumGirisi(eleman: IYollukEleman, sonuc: IGostergeSonuc, geo: ICozumGeometri): IYollukDogrulama {
    const boyut = geo.bbox.getSize(this._v);
    const koseg = boyut.length();
    const yakinInce = sonuc.bolgeler.some(
      b => b.tip === 'ince_cidar' && b.merkez.distanceTo(eleman.pozisyon) <= koseg * 0.15,
    );
    if (yakinInce) {
      return { gecerli: false, mesaj: 'Giriş ince cidardan başlıyor — soğuk birleşme riski, kalın kesite alın' };
    }
    return { gecerli: true, mesaj: 'Üst-merkezden dolum, akış aşağı yönlü ✓' };
  }

  /**
   * En kritik bölgeleri seç: şiddete göre azalan sırada, birbirinden minMesafe
   * uzak olacak şekilde en fazla maxSayi adet (üst üste binen besleyici/delik olmasın).
   */
  private enKritikSec(bolgeler: IDokumBolge[], maxSayi: number, minMesafe: number): IDokumBolge[] {
    const sirali = [...bolgeler].sort((a, b) => (b.siddet ?? 0) - (a.siddet ?? 0));
    const secilen: IDokumBolge[] = [];
    const m2 = minMesafe * minMesafe;
    for (const b of sirali) {
      if (secilen.length >= maxSayi) break;
      if (secilen.some(s => s.merkez.distanceToSquared(b.merkez) < m2)) continue;
      secilen.push(b);
    }
    return secilen;
  }

  private modulDenCap(b: IDokumBolge): { cap: number; yukseklik: number } {
    const modulCm = b.birim === 'cm' ? b.deger : b.deger / 10;
    let cap = Math.max(VARSAYILAN_CAP_MM, modulCm * 10 * 6 * BESLEYICI_MODUL_KATSAYI);
    cap = Math.min(cap, this.parcaMaxCap); // parça-göreli üst sınır (dev besleyici çizme)
    return { cap: Math.round(cap * 10) / 10, yukseklik: Math.round(cap * VARSAYILAN_YUKSEKLIK_ORANI * 10) / 10 };
  }

  private besleyiciOlculeri(
    geo: ICozumGeometri,
    alasim: string,
    sicakNoktalar: IDokumBolge[],
  ): Array<{ cap: number; yukseklik: number }> {
    if (sicakNoktalar.length === 0) return [];
    try {
      const mesh = this.geoMesh(geo);
      const sonuc = this.gating.hesapla(mesh, this.malzemeId(alasim), 'kum', 700);
      mesh.geometry.dispose();
      if (!sonuc.besleyiciler.length) {
        return sicakNoktalar.map(b => this.modulDenCap(b));
      }
      // Her sıcak noktaya, en yakın gating besleyicisinin ölçüsünü ata
      return sicakNoktalar.map(b => {
        let enYakin = sonuc.besleyiciler[0];
        let enKisa = Infinity;
        for (const bes of sonuc.besleyiciler) {
          const d = bes.pozisyon.distanceTo(b.merkez);
          if (d < enKisa) { enKisa = d; enYakin = bes; }
        }
        const cap = Math.min(Math.max(VARSAYILAN_CAP_MM, enYakin.cap), this.parcaMaxCap);
        return { cap, yukseklik: Math.min(enYakin.yukseklik, this.parcaMaxCap * 1.6) };
      });
    } catch {
      return sicakNoktalar.map(b => this.modulDenCap(b));
    }
  }

  private geoMesh(geo: ICozumGeometri): THREE.Mesh {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(geo.positions, 3));
    g.setIndex(new THREE.BufferAttribute(geo.indices, 1));
    return new THREE.Mesh(g);
  }

  private malzemeId(alasim: string): string {
    const a = (alasim || '').toLowerCase();
    if (a.includes('mg') || a.includes('magn')) return 'Mg_AZ91';
    if (a.includes('zn') || a.includes('çin') || a.includes('cin') || a.includes('zamak')) return 'Zn_Zamak5';
    if (a.includes('pirin') || a.includes('cuzn')) return 'CuZn33';
    if (a.includes('bronz') || a.includes('cu') || a.includes('bak')) return 'CuSn10';
    if (a.includes('çelik') || a.includes('celik') || a.includes('steel')) return 'GP240GH';
    if (a.includes('demir') || a.includes('dökme') || a.includes('iron') || a.includes('gg')) return 'GGG40';
    return 'Al_A356';
  }
}
